import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Card,
  CardHeader,
  CardContent,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Avatar,
  Typography,
  Chip,
  Box,
  CircularProgress,
  Alert,
  Divider
} from '@mui/material';
import {
  Login,
  Update,
  Delete,
  Add,
  Person,
  Security,
  Error as ErrorIcon,
  Warning,
  Info
} from '@mui/icons-material';
import api from '../services/api';
import { formatTimeAgo } from '../utils/dateUtils';

const ActivityLogsCard = ({ limit = 10 }) => {
  const navigate = useNavigate();
  const [logs, setLogs] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const fetchDashboardLogs = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get(`/sites/nerv/dashboard-logs?limit=${limit}`);
      setLogs(response.data);
    } catch (error) {
      setError('Failed to load activity logs');
      console.error('Error fetching dashboard logs:', error);
    } finally {
      setLoading(false);
    }
  }, [limit]);

  useEffect(() => {
    fetchDashboardLogs();
  }, [fetchDashboardLogs]);

  const getActivityIcon = (activityType) => {
    const iconMap = {
      'login': <Login />,
      'logout': <Person />,
      'site_create': <Add />,
      'site_update': <Update />,
      'site_delete': <Delete />,
      'plugin_check': <Security />,
      'test': <Info />
    };
    return iconMap[activityType] || <Info />;
  };

  const getSystemLogIcon = (level) => {
    const iconMap = {
      'error': <ErrorIcon />,
      'critical': <ErrorIcon />,
      'warning': <Warning />,
      'info': <Info />
    };
    return iconMap[level] || <Info />;
  };

  const getStatusColor = (status) => {
    const colorMap = {
      'success': 'success',
      'failure': 'error',
      'error': 'error'
    };
    return colorMap[status] || 'default';
  };

  const getLogLevelColor = (level) => {
    const colorMap = {
      'error': 'error',
      'critical': 'error',
      'warning': 'warning',
      'info': 'info'
    };
    return colorMap[level] || 'default';
  };

  const formatTimeago = (dateString) => {
    return formatTimeAgo(dateString);
  };

  if (loading) {
    return (
      <Card elevation={0} sx={{ border: '1px solid', borderColor: 'grey.200' }}>
        <CardHeader title="Recent Activity" subheader="Platform activity logs" />
        <CardContent>
          <Box display="flex" justifyContent="center" py={3}>
            <CircularProgress />
          </Box>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card elevation={0} sx={{ border: '1px solid', borderColor: 'grey.200' }}>
        <CardHeader title="Recent Activity" subheader="Platform activity logs" />
        <CardContent>
          <Alert severity="error">{error}</Alert>
        </CardContent>
      </Card>
    );
  }

  const { recent_activities = [], recent_system_logs = [], summary = {} } = logs || {};

  return (
    <Card elevation={0} sx={{ border: '1px solid', borderColor: 'grey.200', height: 460, display: 'flex', flexDirection: 'column' }}>
      <CardHeader
        title="Recent Activity"
        subheader="Platform activity and system logs"
        action={
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Chip
              label={`${summary.total_today || 0} activities today`}
              size="small"
              color="primary"
              variant="outlined"
              onClick={() => navigate('/logs')}
              sx={{
                cursor: 'pointer',
                '&:hover': {
                  bgcolor: 'primary.50'
                }
              }}
            />
            {(summary.errors_today || 0) > 0 && (
              <Chip
                label={`${summary.errors_today} errors`}
                size="small"
                color="error"
                variant="outlined"
                onClick={() => navigate('/logs')}
                sx={{
                  cursor: 'pointer',
                  '&:hover': {
                    bgcolor: 'error.50'
                  }
                }}
              />
            )}
          </Box>
        }
      />
      <CardContent sx={{ pt: 0, flex: 1, overflow: 'auto' }}>
        {recent_activities.length === 0 && recent_system_logs.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 3 }}>
            No recent activity to display
          </Typography>
        ) : (
          <List dense>
            {/* Activity Logs */}
            {recent_activities.slice(0, Math.ceil(limit * 0.7)).map((activity, index) => (
              <ListItem key={`activity-${activity.id}`} sx={{ px: 0 }}>
                <ListItemIcon sx={{ minWidth: 40 }}>
                  <Avatar 
                    sx={{ 
                      width: 32, 
                      height: 32, 
                      bgcolor: getStatusColor(activity.status) === 'success' ? 'success.main' : 
                               getStatusColor(activity.status) === 'error' ? 'error.main' : 'primary.main',
                      fontSize: '1rem'
                    }}
                  >
                    {getActivityIcon(activity.activity_type)}
                  </Avatar>
                </ListItemIcon>
                <ListItemText
                  primary={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {activity.description}
                      </Typography>
                      <Chip
                        label={activity.status}
                        size="small"
                        color={getStatusColor(activity.status)}
                        sx={{ fontSize: '0.7rem', height: 20 }}
                      />
                    </Box>
                  }
                  secondary={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                      <Typography variant="caption" color="text.secondary">
                        {activity.user_name || 'System'}
                      </Typography>
                      {activity.target_name && (
                        <>
                          <Typography variant="caption" color="text.secondary">•</Typography>
                          <Typography variant="caption" color="text.secondary">
                            {activity.target_name}
                          </Typography>
                        </>
                      )}
                      <Typography variant="caption" color="text.secondary">•</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {formatTimeago(activity.created_at)}
                      </Typography>
                    </Box>
                  }
                />
              </ListItem>
            ))}

            {/* Divider if both types exist */}
            {recent_activities.length > 0 && recent_system_logs.length > 0 && (
              <Divider sx={{ my: 1 }} />
            )}

            {/* System Logs */}
            {recent_system_logs.slice(0, Math.floor(limit * 0.3)).map((sysLog, index) => (
              <ListItem key={`system-${sysLog.id}`} sx={{ px: 0 }}>
                <ListItemIcon sx={{ minWidth: 40 }}>
                  <Avatar 
                    sx={{ 
                      width: 32, 
                      height: 32, 
                      bgcolor: getLogLevelColor(sysLog.level) === 'error' ? 'error.main' : 
                               getLogLevelColor(sysLog.level) === 'warning' ? 'warning.main' : 'info.main',
                      fontSize: '1rem'
                    }}
                  >
                    {getSystemLogIcon(sysLog.level)}
                  </Avatar>
                </ListItemIcon>
                <ListItemText
                  primary={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {sysLog.message.length > 50 ? `${sysLog.message.substring(0, 50)}...` : sysLog.message}
                      </Typography>
                      <Chip
                        label={sysLog.level.toUpperCase()}
                        size="small"
                        color={getLogLevelColor(sysLog.level)}
                        sx={{ fontSize: '0.7rem', height: 20 }}
                      />
                    </Box>
                  }
                  secondary={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                      {sysLog.module && (
                        <>
                          <Typography variant="caption" color="text.secondary">
                            {sysLog.module}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">•</Typography>
                        </>
                      )}
                      <Typography variant="caption" color="text.secondary">
                        {formatTimeago(sysLog.created_at)}
                      </Typography>
                    </Box>
                  }
                />
              </ListItem>
            ))}
          </List>
        )}
      </CardContent>
    </Card>
  );
};

export default ActivityLogsCard;